import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Users, 
  Mic, 
  Heart, 
  Ticket,
  RefreshCw,
  CheckCircle,
  Clock,
  Mail,
  Building2,
  Calendar,
  Camera,
  ImageOff,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [applications, setApplications] = useState({
    speakers: [],
    mentors: [],
    delegates: [],
    totals: { speakers: 0, mentors: 0, delegates: 0 }
  });
  const [photoStatus, setPhotoStatus] = useState({
    total_profiles: 0,
    with_photos: 0,
    without_photos: 0,
    profiles_needing_photos: []
  });
  const [activeTab, setActiveTab] = useState("speakers");
  const [loading, setLoading] = useState(true);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const [appResponse, photoResponse] = await Promise.all([
        fetch(`${API}/admin/applications`),
        fetch(`${API}/admin/photo-status`)
      ]);
      const appData = await appResponse.json();
      const photoData = await photoResponse.json();
      setApplications(appData);
      setPhotoStatus(photoData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === "paid") {
      return (
        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-heading flex items-center gap-1">
          <CheckCircle size={12} /> PAID
        </span>
      );
    }
    if (status === "pending") {
      return (
        <span className="px-2 py-1 bg-gold-500/20 text-gold-400 text-xs font-heading flex items-center gap-1">
          <Clock size={12} /> PENDING
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-white/10 text-white/50 text-xs font-heading">
        {status?.toUpperCase() || "SUBMITTED"}
      </span>
    );
  };

  const tabs = [
    { id: "speakers", label: "Speakers", icon: Mic, count: applications.totals.speakers, color: "gold" },
    { id: "mentors", label: "Mentors", icon: Heart, count: applications.totals.mentors, color: "teal" },
    { id: "delegates", label: "Delegates", icon: Ticket, count: applications.totals.delegates, color: "burgundy" },
    { id: "photos", label: "Photos Needed", icon: ImageOff, count: photoStatus.without_photos, color: "orange" }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
          <div>
            <span className="px-3 py-1 bg-gold-500 text-[#0a0a0a] font-heading font-bold text-xs tracking-widest mb-4 inline-block">
              ADMIN
            </span>
            <h1 className="font-serif text-white text-3xl md:text-4xl font-bold">
              Edition 001 Applications
            </h1>
            <p className="font-body text-white/50 mt-2">
              Manage speaker, mentor, and delegate applications
            </p>
          </div>
          <Button
            onClick={fetchApplications}
            disabled={loading}
            className="mt-4 md:mt-0 rounded-none bg-white/10 text-white hover:bg-white/20 font-heading"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={`p-6 border ${
                activeTab === tab.id ? `border-${tab.color}-500` : "border-[#1A1A1A]/10"
              } bg-[#0f0f0f] cursor-pointer hover:border-${tab.color}-500/50 transition-all`}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`stat-${tab.id}`}
            >
              <div className="flex items-center justify-between">
                <tab.icon className={`text-${tab.color}-500`} size={24} />
                <span className={`font-heading text-${tab.color}-500 text-3xl font-bold`}>
                  {tab.count}
                </span>
              </div>
              <p className="font-heading text-white text-sm mt-4">{tab.label}</p>
              <p className="font-body text-white/50 text-xs">Total applications</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#1A1A1A]/10 mb-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-4 font-heading text-sm tracking-wider transition-all border-b-2 flex items-center gap-2 ${
                activeTab === tab.id
                  ? `text-${tab.color}-500 border-${tab.color}-500`
                  : "text-white/50 border-transparent hover:text-white/80"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
              <span className={`px-2 py-0.5 text-xs ${
                activeTab === tab.id ? `bg-${tab.color}-500/20` : "bg-white/10"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Applications Table */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="animate-spin text-gold-500 mx-auto mb-4" size={32} />
            <p className="font-body text-white/50">Loading applications...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activeTab === "speakers" && (
              <>
                {applications.speakers.length === 0 ? (
                  <div className="text-center py-16 border border-[#1A1A1A]/10 bg-[#0f0f0f]">
                    <Mic className="text-white/20 mx-auto mb-4" size={48} />
                    <p className="font-body text-white/50">No speaker applications yet</p>
                  </div>
                ) : (
                  applications.speakers.map((app) => (
                    <div key={app.id} className="p-6 border border-[#1A1A1A]/10 bg-[#0f0f0f] hover:border-gold-500/30 transition-all" data-testid="speaker-card">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-white text-lg font-bold">{app.name}</h3>
                            {getStatusBadge(app.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-white/60 mb-3">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {app.email}
                            </span>
                            {app.company && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} /> {app.company}
                              </span>
                            )}
                            {app.title && (
                              <span className="text-gold-400">{app.title}</span>
                            )}
                          </div>
                          {app.message && (
                            <p className="font-body text-white/70 text-sm italic border-l-2 border-gold-500/50 pl-4">
                              "{app.message}"
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="flex items-center gap-1 text-white/40 text-xs">
                            <Calendar size={12} /> {formatDate(app.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "mentors" && (
              <>
                {applications.mentors.length === 0 ? (
                  <div className="text-center py-16 border border-[#1A1A1A]/10 bg-[#0f0f0f]">
                    <Heart className="text-white/20 mx-auto mb-4" size={48} />
                    <p className="font-body text-white/50">No mentor applications yet</p>
                  </div>
                ) : (
                  applications.mentors.map((app) => (
                    <div key={app.id} className="p-6 border border-[#1A1A1A]/10 bg-[#0f0f0f] hover:border-teal-500/30 transition-all" data-testid="mentor-card">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-white text-lg font-bold">{app.name}</h3>
                            {getStatusBadge(app.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-white/60 mb-3">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {app.email}
                            </span>
                            {app.company && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} /> {app.company}
                              </span>
                            )}
                            {app.sector && (
                              <span className="text-teal-400">{app.sector}</span>
                            )}
                          </div>
                          {app.message && (
                            <p className="font-body text-white/70 text-sm italic border-l-2 border-teal-500/50 pl-4">
                              "{app.message}"
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="flex items-center gap-1 text-white/40 text-xs">
                            <Calendar size={12} /> {formatDate(app.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "delegates" && (
              <>
                {applications.delegates.length === 0 ? (
                  <div className="text-center py-16 border border-[#1A1A1A]/10 bg-[#0f0f0f]">
                    <Ticket className="text-white/20 mx-auto mb-4" size={48} />
                    <p className="font-body text-white/50">No delegate registrations yet</p>
                  </div>
                ) : (
                  applications.delegates.map((app) => (
                    <div key={app.id} className="p-6 border border-[#1A1A1A]/10 bg-[#0f0f0f] hover:border-burgundy-500/30 transition-all" data-testid="delegate-card">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading text-white text-lg font-bold">{app.name}</h3>
                            {getStatusBadge(app.status, app.payment_status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-white/60">
                            <span className="flex items-center gap-1">
                              <Mail size={14} /> {app.email}
                            </span>
                            {app.company && (
                              <span className="flex items-center gap-1">
                                <Building2 size={14} /> {app.company}
                              </span>
                            )}
                            {app.title && (
                              <span className="text-burgundy-400">{app.title}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="flex items-center gap-1 text-white/40 text-xs">
                            <Calendar size={12} /> {formatDate(app.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </>
            )}

            {activeTab === "photos" && (
              <>
                {/* Photo Stats Banner */}
                <div className="mb-6 p-6 border border-orange-500/30 bg-orange-500/10">
                  <div className="flex flex-wrap gap-8 items-center justify-between">
                    <div>
                      <h3 className="font-heading text-orange-400 text-lg font-bold mb-1">
                        Photo Status Report
                      </h3>
                      <p className="font-body text-white/60 text-sm">
                        {photoStatus.without_photos} profiles need authentic photos
                      </p>
                    </div>
                    <div className="flex gap-6">
                      <div className="text-center">
                        <p className="font-heading text-green-400 text-2xl font-bold">{photoStatus.with_photos}</p>
                        <p className="font-body text-white/50 text-xs">With Photos</p>
                      </div>
                      <div className="text-center">
                        <p className="font-heading text-orange-400 text-2xl font-bold">{photoStatus.without_photos}</p>
                        <p className="font-body text-white/50 text-xs">Need Photos</p>
                      </div>
                      <div className="text-center">
                        <p className="font-heading text-white text-2xl font-bold">{photoStatus.total_profiles}</p>
                        <p className="font-body text-white/50 text-xs">Total</p>
                      </div>
                    </div>
                  </div>
                </div>

                {photoStatus.profiles_needing_photos.length === 0 ? (
                  <div className="text-center py-16 border border-[#1A1A1A]/10 bg-[#0f0f0f]">
                    <Camera className="text-green-500 mx-auto mb-4" size={48} />
                    <p className="font-body text-green-400">All profiles have photos!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {photoStatus.profiles_needing_photos.map((profile) => (
                      <div key={profile.slug} className="p-4 border border-[#1A1A1A]/10 bg-[#0f0f0f] hover:border-orange-500/30 transition-all">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-500/20 flex items-center justify-center text-orange-400">
                            <ImageOff size={18} />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-heading text-white text-sm font-bold">{profile.name}</h4>
                            <p className="font-body text-white/50 text-xs">{profile.region}</p>
                          </div>
                          <Link 
                            to={`/stories/${profile.slug}`}
                            className="text-orange-400 hover:text-orange-300"
                          >
                            <ExternalLink size={16} />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
